import { lazy } from "react";
import {
  type AnyRoute,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { createAssistantsRoutes, PinnedAssistants } from "@/features/assistants";
import { AccountFooter, AccountSection, createAuthRoutes, RequireAuth } from "@/features/auth";
import {
  BillingSection,
  CheckoutReturnHandler,
  createPricingRoute,
  DevMenu,
  PaywallHost,
  UpgradeModalHost,
} from "@/features/billing";
import { ChatTopbarActions, createChatRoutes, createSharedConversationRoute } from "@/features/chat";
import { ConversationCommands, ConversationList } from "@/features/conversations";
import { createImagesRoute } from "@/features/images";
import { createLibraryRoute } from "@/features/library";
import { createSettingsRoute, FontPreferenceSync } from "@/features/settings";
import { AppShell, NotFound, RouteError, RoutePending } from "@/features/shell";
import { createSkillsRoutes } from "@/features/skills";
import { createVideosRoute } from "@/features/videos";
import { createUgcRoutes } from "@/features/ugc";

/**
 * Type-safe route tree. Per-feature route objects compose in via factories
 * (features never import `app`); `app` is the only layer that wires them
 * together. Most routes mount inside the app shell; auth is full-screen.
 */
function RootLayout() {
  return (
    <>
      <Outlet />
      {/* Global billing surfaces: the 402 upgrade modal, the model paywall, and
          the post-checkout return handler. Route-agnostic, so they live at the
          root. */}
      <UpgradeModalHost />
      <PaywallHost />
      <CheckoutReturnHandler />
      {/* DEV-ONLY QA menu — tree-shaken out of production by the `DEV` guard. */}
      {import.meta.env.DEV ? <DevMenu /> : null}
    </>
  );
}

/**
 * Layout route: the auth-gated shell, with the conversation history + account
 * footer (both cross-feature) composed in here — `app` is the only layer that
 * may wire features together.
 */
function ShellLayout() {
  return (
    <RequireAuth>
      {/* Account-synced font → local store (auth-gated by this mount point). */}
      <FontPreferenceSync />
      {/* Recent conversations as ⌘K palette commands (auth-gated, shell-wide). */}
      <ConversationCommands />
      <AppShell
        sidebarHistory={<ConversationList />}
        sidebarPinned={<PinnedAssistants />}
        sidebarFooter={(collapsed) => <AccountFooter collapsed={collapsed} />}
        topbarActions={<ChatTopbarActions />}
      />
    </RequireAuth>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

// "/" → the chat surface (the app's home).
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/chat" });
  },
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: ShellLayout,
});

const shellTree = shellRoute.addChildren([
  ...createChatRoutes(shellRoute),
  createImagesRoute(shellRoute),
  createVideosRoute(shellRoute),
  createLibraryRoute(shellRoute),
  ...createUgcRoutes(shellRoute),
  ...createAssistantsRoutes(shellRoute),
  ...createSkillsRoutes(shellRoute),
  createSettingsRoute(shellRoute, {
    accountSection: <AccountSection />,
    billingSection: <BillingSection />,
  }),
  createPricingRoute(shellRoute),
]);

/**
 * DEV-ONLY component playground at `/playground` — standalone (no shell/auth).
 *
 * Both the dynamic `import()` of the page AND the `createRoute` call live inside
 * this `import.meta.env.DEV` branch. `DEV` is a compile-time constant, so in a
 * production build Rollup proves the branch dead, drops the `import()`
 * expression entirely, and never emits a chunk for `./playground/*`
 * (PlaygroundPage, ImageGenLoaders) — it is fully tree-shaken. The loaders use
 * inline `style`/`<style>` (not Tailwind arbitrary utilities) so nothing
 * playground-specific leaks into the prod CSS bundle either.
 */
function devPlaygroundRoutes(parent: AnyRoute): AnyRoute[] {
  if (!import.meta.env.DEV) return [];
  const PlaygroundPage = lazy(() =>
    import("./playground/PlaygroundPage").then((m) => ({ default: m.PlaygroundPage })),
  );
  return [createRoute({ getParentRoute: () => parent, path: "/playground", component: PlaygroundPage })];
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  shellTree,
  createSharedConversationRoute(rootRoute),
  ...createAuthRoutes(rootRoute),
  ...devPlaygroundRoutes(rootRoute),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultErrorComponent: RouteError,
  defaultPendingComponent: RoutePending,
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
